import { useEffect, useState } from 'react';
import { Button, Modal } from '@skupreme/shared/ui';
import { ToastMessage } from '@utility/toast';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { ErrorMessageConstant, POStatus } from '@utility/constants/common';
import { ControlledTextAreaFormField } from '@elements/FormField';
import PurchaseOrdersService from '@utility/services/purchase-order';
import { useRefreshSidebar } from '@contexts/sidebar';
import { LoaderBar } from '@components/LoaderBar';
import { AppModalHeader, AppModalXIcon } from '@elements/Modal';

const popupSchema = yup.object().shape({
  comment: yup.string(),
});

const AcceptRejectText = {
  [POStatus.open]: {
    title: 'Add Confirmation Note',
    text: 'Provide an internal note for confirming and overriding supplier confirmation.',
  },
  [POStatus.pendingPayment]: {
    title: 'Initial Document Note',
    text: 'Provide an internal note for manually accepting the initial payment document.',
  },
  [POStatus.inProduction]: {
    title: 'Add PO Status Note',
    text: 'Provide an internal note for manually overriding the purchase order status.',
  },
  [POStatus.pendingFinalPayment]: {
    title: 'Add Final Payment Documents Note',
    text: 'Provide an internal note for manually overriding the final payment document review.',
  },
};

export function AcceptRejectPopup({ popupStatus, data, open, closeModal }) {
  const {
    formState: { errors },
    control,
    handleSubmit,
    reset,
  } = useForm({
    resolver: yupResolver(popupSchema),
    shouldUnregister: true,
  });

  useEffect(() => {
    reset({});
  }, [open]);

  const [saving, setSaving] = useState(false);
  const refreshSidebar = useRefreshSidebar();

  const onSubmit = async formData => {
    try {
      setSaving(true);
      await PurchaseOrdersService.interalAction(data?._id, {
        action_status: data?.status,
        action: popupStatus,
        refresh: false,
        comment: formData?.comment,
      });
      refreshSidebar();
      reset({});
      closeModal(true);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => !isOpen && closeModal()}
      size="md"
      isDismissable={false}
      aria-label={AcceptRejectText[data?.status]?.title || 'Add Note'}
    >
      <div className="sk:relative">
        {saving && <LoaderBar content="Saving" />}
        <AppModalHeader className="sk:items-center">
          <div>
            {AcceptRejectText[data?.status]?.title}{' '}
            <span className="sk:font-normal sk:text-fg-tertiary">
              (Optional)
            </span>
          </div>
          <AppModalXIcon onClick={() => closeModal()} />
        </AppModalHeader>
        <form
          autoComplete="off"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          className="sk:flex sk:flex-col"
        >
          <p className="sk:my-4 sk:max-w-100 sk:text-sm sk:text-fg-secondary">
            {AcceptRejectText[data?.status]?.text}
          </p>
          <ControlledTextAreaFormField
            error={errors.comment}
            placeholder="Internal Note"
            name="comment"
            control={control}
          />
          <div className="sk:flex sk:justify-end sk:gap-2">
            <Button onPress={() => onSubmit({})}>Skip</Button>
            <Button type="submit" variant="primary">
              Add Note
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
